// components/radiologyTechnicians/AddTechnicianDialog.jsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { technicianAPI } from '../../services/api';
import { radiologyTechnicianSchema } from '../../validations/schemas';

const AddTechnicianDialog = ({ open, onClose, selectedTechnician, onSuccess }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: selectedTechnician?.name || '',
            age: selectedTechnician?.age || '',
        },
        validationSchema: radiologyTechnicianSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                setLoading(true);

                const payload = {
                    name: values.name.trim(),
                    age: Number(values.age)
                };

                if (selectedTechnician) {
                    await technicianAPI.update(selectedTechnician._id, payload);
                    toast.success(t('radiologyTechnicians.updateSuccess', 'Technician updated successfully'));
                } else {
                    await technicianAPI.create(payload);
                    toast.success(t('radiologyTechnicians.createSuccess', 'Technician created successfully'));
                }

                onSuccess();
                handleClose();
                resetForm();
            } catch (err) {
                console.error('Technician save error:', err);
                toast.error(err.response?.data?.message || t('radiologyTechnicians.error', 'An error occurred'));
            } finally {
                setLoading(false);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    // Reset form when dialog opens/closes or selectedTechnician changes
    useEffect(() => {
        if (open) {
            formik.setValues({
                name: selectedTechnician?.name || '',
                age: selectedTechnician?.age || '',
            });
        }
    }, [open, selectedTechnician]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>
                    {selectedTechnician
                        ? t('radiologyTechnicians.editTitle', 'Edit Technician')
                        : t('radiologyTechnicians.addTitle', 'Add Technician')
                    }
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="name"
                                label={t('radiologyTechnicians.name', 'Name')}
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                                required
                                autoFocus
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="age"
                                label={t('radiologyTechnicians.age', 'Age')}
                                type="number"
                                value={formik.values.age}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.age && Boolean(formik.errors.age)}
                                helperText={formik.touched.age && formik.errors.age}
                                required
                                inputProps={{ min: 18, max: 100 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} disabled={loading}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !formik.isValid}
                    >
                        {loading ? (
                            <CircularProgress size={20} />
                        ) : selectedTechnician ? (
                            t('common.update', 'Update')
                        ) : (
                            t('common.create', 'Create')
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddTechnicianDialog;